import React, { Suspense } from 'react'
import pRetry from 'p-retry'
import { track as trackError } from 'lib/utils/errors'
import LoadingPlaceholder from 'components/LoadingPlaceholder'
import ErrorBoundary from 'components/ErrorBoundary'
import ErrorMessage from 'components/ErrorMessage'

function ReloadPagePrompt() {
  return (
    <ErrorMessage
      title="Couldn't load page"
      messaging="An error occurred while loading this page."
      primaryButton={{
        children: 'Reload',
        onClick: () => window.location.reload()
      }}
    />
  )
}

export default function withLoadable(loader) {
  function retryLoader() {
    return loader().catch(error => {
      // Only retry network errors because webpack will just return an empty module
      // when you try re-importing a module that failed to load due to a javascript error
      if (typeof error.request !== 'string') {
        // Track the javascript error
        trackError(error)
        throw new pRetry.AbortError(error)
      }

      // Don't retry css loading errors because webpack won't retry fetching them
      if (!error.request.endsWith('.js')) {
        throw new pRetry.AbortError(error)
      }

      throw error
    })
  }

  const AsyncComponent = React.lazy(() => pRetry(retryLoader, { retries: 3, minTimeout: 300 }))

  return class WithSuspense extends React.Component {
    static preload() {
      loader()
    }

    render() {
      return (
        <ErrorBoundary errorComponent={ReloadPagePrompt}>
          <Suspense fallback={<LoadingPlaceholder delay={200} isLoading />}>
            <AsyncComponent {...this.props} />
          </Suspense>
        </ErrorBoundary>
      )
    }
  }
}
